import React, {useEffect, useMemo, useState} from "react";
import Markdown from "react-markdown";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {config} from "../../utils2";
import remarkGfm from "remark-gfm";


export function RenderMarkdownGitHub({github, shrink = false}) {
    const [markdownContent, setMarkdownContent] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Normalize input (either raw GitHub URL or "/git/..." path)
    const resolveGithubUrl = (input) => {
        // If it already looks like a raw GitHub URL, use it directly
        if (input.startsWith("http")) {
            return input;
        }

        // Otherwise, assume it's a local "/git/..." path
        // Strip the leading "/git/" and append to GitHub repo path
        const relativePath = input.replace(/^\/?git\//, "");
        return `https://raw.githubusercontent.com/altrabits/AB_Documents/main/${relativePath}`;
    };

    useEffect(() => {
        const githubUrl = resolveGithubUrl(github);

        fetch(githubUrl)
            .then(response => {
                if (!response.ok) throw new Error(`Failed to load ${githubUrl}`);
                return response.text();
            })
            .then(md => {
                if (shrink) {

                    const sections = md.split('###')
                    const subSections = sections.slice(0, 3).join("###").trim()

                    setMarkdownContent(subSections)
                } else {
                    setMarkdownContent(md)
                }
            })
            .catch(setError)
            .finally(() => setLoading(false));
    }, [github]);

    const children = useMemo(() => <MarkdownRender markdownContent={markdownContent} imgRef={github}/>, [markdownContent])

    return (
        <>
            {error && <div>Error: {error.message}</div>}
            {loading ? (
                <div>Loading...</div>
            ) : children}
        </>
    );
}

export function MarkdownRender({markdownContent, imgRef = null}) {
    return <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
            a: ({href, children}) => {

                if (['youtube', 'embed', "youtu.be"].some((item) => href.includes(item))) {
                    let embedUrl = href;

                    if (href.includes('youtu.be')) {
                        embedUrl = href.replace('youtu.be/', 'www.youtube.com/embed/').split('?')[0];
                    }

                    return (
                        <div style={{
                            position: "relative",
                            paddingBottom: "56.25%",
                            height: 0,
                            overflow: "hidden",
                            borderRadius: "8px",
                        }}>
                            <iframe
                                src={embedUrl}
                                title="YouTube video"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                }}
                            />
                        </div>
                    );
                }

                const navigate = useNavigate();
                const isRelative = !href.startsWith("http")
                const location = useLocation();

                if (isRelative) {
                    let route = ""
                    const match = location.pathname.match(/(.*)\/.*/);
                    if (match && match[1]) {
                        route += match[1]
                    }

                    // TODO. Temporary workaround to handle relative from landing page
                    if (route === "") {
                        route += "/git/Blog"
                    }

                    route += "/" + href

                    return (
                        <Link
                            to={route}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(route);
                            }}
                            style={{cursor: 'pointer'}}
                        >
                            {children}
                        </Link>
                    );
                }

                // fallback for external links
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                );
            },
            img: ({src, alt = ''}) => {
                // Extract styles from alt text
                // Usage: ![A sample image | width=200 height=100](http://example.com/image.jpg)
                const [actualAlt, sizeInfo] = alt.split('|').map(str => str.trim());
                const style = {};

                if (sizeInfo) {
                    const sizeParts = sizeInfo.split(' ');
                    sizeParts.forEach(part => {
                        const [key, value] = part.split('=');
                        if (key && value) {
                            if (['width', 'height', 'maxWidth', 'maxHeight'].includes(key)) {
                                style[key] = /^\d+$/.test(value) ? `${value}px` : value;
                            }
                        }
                    });
                }

                const isRelative = !src.startsWith("http")
                let srcPath = src
                if (isRelative) {
                    if (imgRef == null) {
                        srcPath = `${config.get_remote_api()}${src}`
                    } else {
                        let route = ""
                        const match = imgRef.match(/(.*)\/.*/);
                        if (match && match[1]) {
                            route += match[1]
                        }
                        srcPath = route + "/" + src
                    }
                }


                return (
                    <img
                        src={srcPath}
                        alt={actualAlt}
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            ...style,
                        }}
                    />
                );
            },

            table: ({children}) => (
                <table className="table-auto border-collapse border border-gray-300 w-full my-4">
                    {children}
                </table>
            ),
            thead: ({children}) => <thead>{children}</thead>,
            tbody: ({children}) => <tbody>{children}</tbody>,
            tr: ({children}) => <tr className="border-b border-gray-300">{children}</tr>,
            th: ({children}) => (
                <th className="border border-gray-300 px-4 py-2" style={{backgroundColor: "#0047ab", color: "white"}}>{children}</th>
            ),
            td: ({children}) => (
                <td className="border border-gray-300 px-4 py-2">{children}</td>
            ),

        }}
    >
        {markdownContent}
    </Markdown>
}


export function RenderMarkdownGitHubForLandingTrue({github, routerPath}) {
    const [markdownContent, setMarkdownContent] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const resolveGithubUrl = (input) => {
        if (!input) return "";
        if (input.startsWith("http")) return input;
        const relativePath = input.replace(/^\/?git\//, "");
        return `https://raw.githubusercontent.com/altrabits/AB_Documents/main/${relativePath}`;
    };

    useEffect(() => {
        const githubUrl = resolveGithubUrl(github);

        fetch(githubUrl)
            .then(response => {
                if (!response.ok) throw new Error(`Failed to load ${githubUrl}`);
                return response.text();
            })
            .then(text => {
                // Fix relative image paths
                const baseUrl = githubUrl.substring(0, githubUrl.lastIndexOf('/') + 1);
                let processedText = text.replace(
                    /!\[([^\]]*)\]\((?!http)([^)]+)\)/g,
                    (match, alt, src) => `![${alt}](${baseUrl}${src})`
                );

                // Extract content before third ### heading
                const headingMatches = [...processedText.matchAll(/^###\s+/gm)];
                if (headingMatches.length >= 2) {
                    const thirdHeadingIndex = headingMatches[2]?.index;
                    if (thirdHeadingIndex !== undefined) {
                        processedText = processedText.slice(0, thirdHeadingIndex);
                    }
                }

                setMarkdownContent(processedText);
            })
            .catch(setError)
            .finally(() => setLoading(false));
    }, [github]);

    return (
        <>
            {error && <div>Error: {error.message}</div>}
            {loading ? (
                <div>Loading...</div>
            ) : (
                <Markdown
                    components={{
                        a: ({href, children}) => {
                            const isRelative = !href.startsWith("http");
                            if (isRelative && routerPath) {
                                const newHref = href.replace(/^\./gm, "").replace(/^\//gm, "");
                                const route = routerPath.replace(/\/$/gm, "") + "/" + newHref;
                                return (
                                    <a
                                        href={route}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(route);
                                        }}
                                        style={{cursor: "pointer"}}
                                    >
                                        {children}
                                    </a>
                                );
                            }
                            return (
                                <a href={href} target="_blank" rel="noopener noreferrer">
                                    {children}
                                </a>
                            );
                        },
                        img: ({src, alt}) => (
                            <img
                                src={src}
                                alt={alt}
                                style={{
                                    maxWidth: "400px",
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "12px",
                                    display: "block",
                                    margin: "1.5rem auto",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                }}
                            />
                        ),
                    }}
                >
                    {markdownContent}
                </Markdown>
            )}
        </>
    );
}
